import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Table, Typography, Popover } from "antd";
import { EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import AddCircleIcon from "../../assets/svg/AddCircleIcon";
import styled, { createGlobalStyle } from "styled-components";

import { GRAY_500, GRAY_75, BLACK, PRIMARY_850, WHITE_GRAY, GRAY_100, WHITE } from "../../constant/colors";
import { StatusTag, StyledActionButton, StyledActionButtonNoClick, StyledPrimaryButton } from "./shared/components";
import { selectUser, selectEnvironment, selectUsers } from "../../store/selector";
import { trackPendoEvent } from "../../utils/pendo/pendoUtils";
import EmptyPhysicianListIcon from "../../assets/svg/EmptyPhysicianListIcon";
import { getNamesFromFullName } from "../../component/Patient/ApplicationFormEditor/utils/parsers";
import { dateTimeFormatter } from "../../utils/date";
import { formatPhoneNumber } from "../../utils/formaters";
import { getUserName } from "./shared/utils";

const { Text } = Typography;

const TooltipGlobalStyle = createGlobalStyle`
  .ant-tooltip {
    .ant-tooltip-inner {
      background-color: ${WHITE} !important;
      color: ${BLACK} !important;

      .ant-tooltip-inner-content {
        padding: 6px 8px;
      }
    }

    .ant-tooltip-arrow-content {
      --antd-arrow-background-color: ${WHITE} !important;
      background-color: ${WHITE} !important;
    }
  }

  .ant-popover {
    .ant-popover-inner-content {
      padding: 6px 8px;
    }
  }
`;

const StyledTable = styled(Table)`
  .ant-table-body {
    overflow-y: overlay !important;
  }

  .ant-table-thead
    > tr
    > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
    background: none;
  }

  .ant-table-thead > tr > th {
    background: ${GRAY_100};
    color: ${BLACK};
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.5px;
    padding: 10px 12px;
    border: none !important;
  }

  .ant-table-thead > tr:hover > th {
    background: ${GRAY_100} !important;
  }

  .ant-table-tbody > tr > td {
    padding: 10px 12px;
    color: ${BLACK};
  }

  .ant-table-tbody > tr {
    cursor: pointer;

    &:hover {
      background-color: ${GRAY_75};
    }
  }

  .ant-pagination {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 24px;
    gap: 8px;

    .ant-pagination-total-text {
      font-size: 14px;
      color: ${BLACK};
      margin-right: 16px;
      min-width: 250px;
      display: inline-block;
    }

    .ant-pagination-item {
      border: none;
      background: transparent;
      min-width: 32px;
      height: 32px;
      line-height: 32px;
      border-radius: 4px;

      a {
        color: ${BLACK};
      }

      &:hover {
        background: ${WHITE_GRAY};
      }

      &.ant-pagination-item-active {
        background: ${PRIMARY_850};
        border-radius: 50%;

        a {
          color: white;
        }

        &:hover {
          background: ${PRIMARY_850};
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      min-width: 32px;
      height: 32px;
      line-height: 32px;
      border: none;

      button {
        border: none;
        background: transparent;

        &:hover {
          background: ${WHITE_GRAY};
          border-radius: 4px;
        }
      }
    }

    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      min-width: 32px;
      height: 32px;
      line-height: 32px;

      .ant-pagination-item-link {
        border: none;
        background: transparent;
      }
    }
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  gap: 16px;
  cursor: default;
`;

const EmptyStateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  svg {
    width: 64px;
    height: 64px;
  }
`;

const EmptyStateTitle = styled(Text)`
  font-size: 18px;
  font-weight: 600;
  color: ${BLACK};
  margin-bottom: 0px;
`;

const EmptyStateDescription = styled(Text)`
  font-size: 14px;
  color: ${GRAY_500};
  margin-bottom: 0px;
`;

const EmptyStateButton = styled(StyledPrimaryButton)`
  margin-top: 8px;
`;

const EmptyStateBulkText = styled.p`
  font-family: "Open Sans";
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  color: ${BLACK};
  text-align: center;
  margin: 8px 0 0 0;

  a {
    color: #1890ff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const NameIdCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 200px;
`;

const NameText = styled(Text)`
  font-family: "Open Sans";
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: ${BLACK};
  display: block;
  max-width: 200px;
`;

const NpiText = styled.div`
  font-size: 14px;
  color: ${BLACK};
  line-height: 1.15;
`;

const AddressCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 200px;
`;

const AddressLine = styled(Text)`
  font-size: 14px;
  color: ${BLACK};
  line-height: 1.2;
  display: block;
  max-width: 200px;
`;

const FacilityText = styled(Text)`
  font-size: 14px;
  color: ${BLACK};
  display: block;
  max-width: 200px;
`;

const EmailText = styled(Text)`
  font-size: 14px;
  color: ${BLACK};
  display: block;
  max-width: 180px;
`;

const PhysicianList = ({
  physicians,
  totalPhysicianCount = 0,
  onEdit,
  onViewDetails,
  onAddPhysician,
  isSearchResult = false,
  currentPage = 1,
  onPageChange,
  sortColumn = null,
  sortOrder = null,
  onSortChange,
  hasBothActiveAndInactive = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const users = useSelector(selectUsers) || {};
  const pageSize = 25;

  const getUpdateInfo = (physician) => {
    const userName = getUserName(
      users,
      physician.updatedBy || physician.createdBy,
      t("physician_management.detail.default_user")
    );
    const updateDate = physician.userUpdatedAt || physician.createdAt;
    if (!updateDate) return t("physician_management.list.no_update_information");

    const formattedDate = dateTimeFormatter(updateDate).replace(" ", " – ");
    return `Last updated by: ${userName} on ${formattedDate}`;
  };

  const getFormattedName = (name) => {
    const { firstName, lastName } = getNamesFromFullName(name);
    if (firstName && lastName) {
      return `${lastName}, ${firstName}`;
    }
    return name;
  };

  const columns = [
    {
      title: t("physician_management.list.name_id"),
      dataIndex: "name",
      key: "nameId",
      width: 220,
      sorter: (a, b) => (a.npi || "").localeCompare(b.npi || ""),
      sortOrder: sortColumn === "nameId" ? sortOrder : null,
      render: (name, record) => {
        const formattedName = getFormattedName(name) || "-";
        return (
          <NameIdCell>
            <NameText ellipsis={{ tooltip: formattedName }}>{formattedName}</NameText>
            <NpiText>NPI {record.npi || "-"}</NpiText>
          </NameIdCell>
        );
      }
    },
    {
      title: t("physician_management.list.facility"),
      dataIndex: "facility",
      key: "facility",
      width: 220,
      render: (facility) => {
        if (!facility) return "-";
        return <FacilityText ellipsis={{ tooltip: facility }}>{facility}</FacilityText>;
      }
    },
    {
      title: t("physician_management.list.address"),
      dataIndex: "address",
      key: "address",
      width: 220,
      render: (address, record) => {
        const hasAddress = record.address || record.city || record.state || record.zipCode;
        if (!hasAddress) {
          return "-";
        }
        const addressLine =
          `${record.address || ""}${record.suiteNumber ? `, ${record.suiteNumber}` : ""}`.trim() || "-";
        const cityStateZip = `${[record.city, record.state].filter(Boolean).join(", ")} ${record.zipCode || ""}`.trim();
        return (
          <AddressCell>
            <AddressLine ellipsis={{ tooltip: addressLine }}>{addressLine}</AddressLine>
            <AddressLine ellipsis={{ tooltip: cityStateZip || "-" }}>{cityStateZip || "-"}</AddressLine>
          </AddressCell>
        );
      }
    },
    {
      title: t("physician_management.list.phone_number"),
      dataIndex: "phone",
      key: "phone",
      width: 150,
      render: (phone) => (phone ? formatPhoneNumber(phone) : "-")
    },
    {
      title: t("physician_management.list.email"),
      dataIndex: "email",
      key: "email",
      width: 200,
      render: (email) => {
        if (!email) return "-";
        return <EmailText ellipsis={{ tooltip: email }}>{email}</EmailText>;
      }
    },
    {
      title: t("physician_management.list.active"),
      dataIndex: "isActive",
      key: "isActive",
      width: 90,

      sorter: hasBothActiveAndInactive
        ? (a, b) => {
            // Sort active first, then inactive
            if (a.isActive === b.isActive) return 0;
            return a.isActive ? -1 : 1;
          }
        : false,
      sortOrder: sortColumn === "isActive" ? sortOrder : null,
      render: (isActive) => (
        <StatusTag padding="2px 10px" className={isActive ? "active-tag" : "inactive-tag"}>
          {isActive ? t("physician_management.list.active_status") : t("physician_management.list.inactive_status")}
        </StatusTag>
      )
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <Popover content={t("physician_management.list.edit_tooltip")}>
            <StyledActionButton
              id="Physician_MainView_Edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(record);
              }}
            />
          </Popover>
          <Popover
            content={getUpdateInfo(record)}
            overlayInnerStyle={{ padding: "6px 8px !important" }}
            placement="topRight"
          >
            <StyledActionButtonNoClick
              type="primary"
              icon={<InfoCircleOutlined />}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </Popover>
        </div>
      )
    }
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    // Track Pendo event when sorting by Name/NPI column
    if (sorter && sorter.columnKey === "nameId" && sorter.order) {
      try {
        trackPendoEvent(dispatch, "Physician_SortByNPI", {
          username: user?.email,
          account,
          sortOrder: sorter.order
        });
      } catch (error) {
        console.error("Error dispatching Pendo event:", error);
      }
    }

    // Track Pendo event when sorting by Active column
    if (sorter && sorter.columnKey === "isActive" && sorter.order) {
      try {
        trackPendoEvent(dispatch, "Physician_SortByStatus", {
          username: user?.email,
          account,
          sortOrder: sorter.order
        });
      } catch (error) {
        console.error("Error dispatching Pendo event:", error);
      }
    }

    // Persist sort state
    if (onSortChange) {
      onSortChange(sorter?.columnKey || null, sorter?.order || null);
    }
  };

  const emptyState = () => {
    // Show search no results state when searching returns no results
    if (isSearchResult) {
      return (
        <EmptyStateContainer>
          <EmptyStateIcon>
            <EmptyPhysicianListIcon />
          </EmptyStateIcon>
          <EmptyStateTitle>{t("physician_management.list.search_empty_title")}</EmptyStateTitle>
          <EmptyStateDescription>{t("physician_management.list.search_empty_description")}</EmptyStateDescription>
          <EmptyStateButton
            id="Physician_SearchResults_NoPhysicians_AddPhysician"
            type="primary"
            icon={<AddCircleIcon />}
            onClick={onAddPhysician}
          >
            {t("physician_management.list.add_physician")}
          </EmptyStateButton>
        </EmptyStateContainer>
      );
    }

    // Determine the title and description based on whether there are inactive physicians
    const hasOnlyInactive = totalPhysicianCount > 0;
    const emptyTitle = hasOnlyInactive
      ? t("physician_management.list.no_active_physicians_title")
      : t("physician_management.list.empty_state_title");
    const emptyDescription = hasOnlyInactive
      ? t("physician_management.list.no_active_physicians_description")
      : t("physician_management.list.empty_state_description");

    // Fallback for empty state
    return (
      <EmptyStateContainer>
        <EmptyStateIcon>
          <EmptyPhysicianListIcon />
        </EmptyStateIcon>
        <EmptyStateTitle>{emptyTitle}</EmptyStateTitle>
        <EmptyStateDescription>{emptyDescription}</EmptyStateDescription>
        <EmptyStateButton
          id="Physician_NoPhysicians_AddButton"
          type="primary"
          icon={<AddCircleIcon />}
          onClick={onAddPhysician}
        >
          {t("physician_management.list.add_physician")}
        </EmptyStateButton>
        <EmptyStateBulkText>
          {t("physician_management.list.empty_state_bulk_upload_text")}{" "}
          <a href="mailto:support@tailormed.co">{t("physician_management.list.empty_state_contact_support")}</a>{" "}
          {t("physician_management.list.empty_state_bulk_upload_suffix")}
        </EmptyStateBulkText>
      </EmptyStateContainer>
    );
  };

  return (
    <>
      <TooltipGlobalStyle />
      <StyledTable
        columns={columns}
        dataSource={physicians}
        rowKey="id"
        showSorterTooltip={false}
        onRow={(record) => ({
          onClick: () => onViewDetails(record)
        })}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: physicians.length,
          showSizeChanger: false,
          showTotal: (total, range) => (
            <span>
              {t("physician_management.list.pagination_showing", {
                range_start: range[0],
                range_end: range[1],
                total
              })}{" "}
              <strong>{t("physician_management.list.physicians_label")}</strong>
            </span>
          ),
          onChange: (page) => onPageChange && onPageChange(page),
          position: ["bottomLeft"]
        }}
        locale={{
          emptyText: emptyState()
        }}
        scroll={{ y: "calc(100vh - 400px)" }}
        sticky
      />
    </>
  );
};

export { PhysicianList };
